import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface QuantTrackerStats {
  passed: number;
  failed: number;
  avgTime: string;      // e.g. "1:40"
  bestTime: string;
  worstTime: string;
  passRate: number;     // 0–100
  totalProblems: number;
}

export interface WeeklyDataPoint {
  label: string;        // e.g. "W1"
  result: 'pass' | 'fail';
  value: number;        // 0–1, bar height proportion
}

export interface QuantTrackerProps {
  stats: QuantTrackerStats;
  weeklyData?: WeeklyDataPoint[];
  onLogResult: (result: 'pass' | 'fail', seconds: number) => void;
  onOpenHistory: () => void;
}

// ── Timer state machine ───────────────────────────────────────────────────────
type TimerPhase = 'idle' | 'running' | 'stopped';
type LoggedSession = { result: 'pass' | 'fail'; seconds: number };

// ── Time string helpers ───────────────────────────────────────────────────────
const parseTimeStr = (t: string): number => {
  if (!t || t === '—') return 0;
  const [m, s] = t.split(':');
  return (parseInt(m) || 0) * 60 + (parseInt(s) || 0);
};
const fmtSecs = (s: number): string =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// ── Blinking colon animation (injected once) ──────────────────────────────────
const BLINK_STYLE = `
  @keyframes qt-blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.5; }
  }
  .qt-colon-blink { animation: qt-blink 1s step-start infinite; }
`;

// ── Component ─────────────────────────────────────────────────────────────────

const QuantTracker: React.FC<QuantTrackerProps> = ({
  stats,
  weeklyData,
  onLogResult,
  onOpenHistory,
}) => {
  const [phase, setPhase] = useState<TimerPhase>('idle');
  const [elapsed, setElapsed] = useState(0);           // seconds
  const [perfOpen, setPerfOpen] = useState(false);
  const [localSessions, setLocalSessions] = useState<LoggedSession[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Derived stats (props baseline + local sessions) ──────────────────────────
  const localPassed = localSessions.filter(s => s.result === 'pass').length;
  const localFailed = localSessions.filter(s => s.result === 'fail').length;
  const dynPassed = stats.passed + localPassed;
  const dynFailed = stats.failed + localFailed;
  const dynTotal  = stats.totalProblems + localSessions.length;
  const dynPassRate = dynTotal > 0 ? Math.round((dynPassed / dynTotal) * 100) : stats.passRate;

  const localTotalSecs = localSessions.reduce((sum, s) => sum + s.seconds, 0);
  const initAvgSecs    = parseTimeStr(stats.avgTime);
  const dynAvgSecs     = dynTotal > 0
    ? Math.round((initAvgSecs * stats.totalProblems + localTotalSecs) / dynTotal)
    : initAvgSecs;
  const dynAvgTime = fmtSecs(dynAvgSecs);

  const localTimes   = localSessions.map(s => s.seconds);
  const initBest     = parseTimeStr(stats.bestTime);
  const initWorst    = parseTimeStr(stats.worstTime);
  const allBests     = [initBest, ...localTimes].filter(Boolean);
  const allWorsts    = [initWorst, ...localTimes];
  const dynBestTime  = allBests.length  ? fmtSecs(Math.min(...allBests))  : stats.bestTime;
  const dynWorstTime = allWorsts.length ? fmtSecs(Math.max(...allWorsts)) : stats.worstTime;

  // ── Timer logic ─────────────────────────────────────────────────────────────

  const startTimer = useCallback(() => {
    setElapsed(0);
    setPhase('running');
  }, []);

  const stopTimer = useCallback(() => {
    setPhase('stopped');
  }, []);

  const restartTimer = useCallback(() => {
    setElapsed(0);
    setPhase('running');
  }, []);

  useEffect(() => {
    if (phase === 'running') {
      intervalRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [phase]);

  // cleanup on unmount
  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const handleLog = (result: 'pass' | 'fail') => {
    setLocalSessions(prev => [...prev, { result, seconds: elapsed }]);
    onLogResult(result, elapsed);
    setElapsed(0);
    setPhase('idle');
  };

  // ── Format timer display ─────────────────────────────────────────────────────
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const timerMins = String(minutes);                    // no leading zero on minutes
  const timerSecs = String(seconds).padStart(2, '0');

  // ── Timer card subtitle ──────────────────────────────────────────────────────
  const subtitle =
    phase === 'idle'    ? 'Time yourself before logging' :
    phase === 'running' ? 'Timer running — solve the problem' :
                          'Log your result below';

  // ── Log button enabled ───────────────────────────────────────────────────────
  const logEnabled = phase === 'running' || phase === 'stopped';

  return (
    <div style={{ padding: '16px 16px 32px', maxWidth: 480, margin: '0 auto' }}>
      <style>{BLINK_STYLE}</style>

      {/* ── Section 1: Stats summary ─────────────────────────────────────────── */}
      <div style={{
        backgroundColor: '#fff',
        border: '0.5px solid rgba(0,0,0,0.07)',
        borderRadius: 16,
        padding: '14px 16px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        marginBottom: 12,
      }}>
        {/* Passed */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#478475', marginBottom: 4 }}>
            Passed
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <span style={{ fontSize: 13, color: '#3B6D11' }}>✓</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#3B6D11', fontVariantNumeric: 'tabular-nums' }}>
              {dynPassed}
            </span>
          </div>
        </div>

        {/* Failed */}
        <div style={{ textAlign: 'center', borderLeft: '1px solid #eef3f1', borderRight: '1px solid #eef3f1' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#478475', marginBottom: 4 }}>
            Failed
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <span style={{ fontSize: 13, color: '#A32D2D' }}>✕</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#A32D2D', fontVariantNumeric: 'tabular-nums' }}>
              {dynFailed}
            </span>
          </div>
        </div>

        {/* Avg time */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#478475', marginBottom: 4 }}>
            Avg Time
          </div>
          <span style={{ fontSize: 22, fontWeight: 800, color: '#1F4C40', fontVariantNumeric: 'tabular-nums' }}>
            {dynAvgTime}
          </span>
        </div>
      </div>

      {/* ── Section 2: Timer card ─────────────────────────────────────────────── */}
      <div style={{
        background: '#1F4C40',
        borderRadius: 20,
        padding: '24px 20px 20px',
        marginBottom: 12,
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
      }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: -32, right: -32, width: 120, height: 120, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -20, width: 160, height: 160, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 20, left: -48, width: 100, height: 100, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />

        {/* Instruction label */}
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9fc9bb', marginBottom: 10 }}>
          Start timer, then solve
        </div>

        {/* Timer digits */}
        <div style={{ fontSize: 64, fontWeight: 800, color: '#fff', fontVariantNumeric: 'tabular-nums', lineHeight: 1, marginBottom: 8, letterSpacing: '-2px' }}>
          {timerMins}
          <span
            className={phase === 'running' ? 'qt-colon-blink' : undefined}
            style={{ color: '#478475', display: 'inline-block', margin: '0 2px' }}
          >:</span>
          {timerSecs}
        </div>

        {/* Subtitle */}
        <div style={{ fontSize: 13, color: '#7aaa96', marginBottom: 18 }}>
          {subtitle}
        </div>

        {/* Start / Stop / Restart button */}
        <button
          onClick={phase === 'idle' ? startTimer : phase === 'running' ? stopTimer : restartTimer}
          style={{
            width: '100%',
            padding: '13px 0',
            borderRadius: 999,
            border: 'none',
            backgroundColor: phase === 'running' ? '#E6F1EC' : '#fff',
            color: '#1F4C40',
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            marginBottom: 12,
          }}
        >
          {phase === 'idle' ? 'Start Timer' : phase === 'running' ? 'Stop Timer' : 'Restart Timer'}
        </button>

        {/* Log buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button
            onClick={() => logEnabled && handleLog('pass')}
            disabled={!logEnabled}
            style={{
              padding: '11px 0',
              borderRadius: 12,
              border: 'none',
              backgroundColor: '#d4f0e4',
              color: '#1a5c35',
              fontSize: 14,
              fontWeight: 700,
              cursor: logEnabled ? 'pointer' : 'default',
              opacity: logEnabled ? 1 : 0.38,
              pointerEvents: logEnabled ? 'auto' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <span>✓</span> Got it
          </button>
          <button
            onClick={() => logEnabled && handleLog('fail')}
            disabled={!logEnabled}
            style={{
              padding: '11px 0',
              borderRadius: 12,
              border: 'none',
              backgroundColor: '#fde8e8',
              color: '#7a2020',
              fontSize: 14,
              fontWeight: 700,
              cursor: logEnabled ? 'pointer' : 'default',
              opacity: logEnabled ? 1 : 0.38,
              pointerEvents: logEnabled ? 'auto' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <span>✕</span> Missed it
          </button>
        </div>
      </div>

      {/* ── Section 3: Performance (collapsible) ─────────────────────────────── */}
      <div style={{
        backgroundColor: '#fff',
        border: '0.5px solid rgba(0,0,0,0.07)',
        borderRadius: 16,
        marginBottom: 12,
        overflow: 'hidden',
      }}>
        {/* Header row */}
        <button
          onClick={() => setPerfOpen(o => !o)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1F4C40' }}>Performance</span>
          <span style={{
            fontSize: 12,
            color: '#478475',
            display: 'inline-block',
            transform: perfOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.25s ease',
          }}>▼</span>
        </button>

        {/* Collapsible body */}
        <div style={{
          maxHeight: perfOpen ? 350 : 0,
          overflow: 'hidden',
          transition: 'max-height 350ms cubic-bezier(0.4,0,0.2,1)',
        }}>
          <div style={{ padding: '0 16px 16px' }}>

            {/* Mini bar chart */}
            {weeklyData && weeklyData.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#478475', marginBottom: 8 }}>
                  Weekly
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 6,
                  height: 60,
                }}>
                  {weeklyData.map((w, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                      <div style={{
                        width: '100%',
                        height: `${Math.max(w.value * 51, 4)}px`,
                        borderRadius: 4,
                        backgroundColor: w.result === 'pass' ? '#3B6D11' : '#A32D2D',
                      }} />
                      <span style={{ fontSize: 9, color: '#478475', fontWeight: 600 }}>{w.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Breakdown 2×2 grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Best time',        value: dynBestTime },
                { label: 'Worst time',       value: dynWorstTime },
                { label: 'Pass rate',        value: `${dynPassRate}%` },
                { label: 'Problems logged',  value: String(dynTotal) },
              ].map(({ label, value }) => (
                <div key={label} style={{ backgroundColor: '#f4f8f5', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#478475', marginBottom: 3 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#1F4C40', fontVariantNumeric: 'tabular-nums' }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 4: Problem history link ──────────────────────────────────── */}
      <button
        onClick={onOpenHistory}
        style={{
          width: '100%',
          backgroundColor: '#fff',
          border: '0.5px solid rgba(0,0,0,0.07)',
          borderRadius: 16,
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1F4C40', marginBottom: 2 }}>
            Problem history
          </div>
          <div style={{ fontSize: 12, color: '#7aaa96' }}>
            Tap to review all attempts
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {dynTotal > 0 && (
            <span style={{
              backgroundColor: '#E1F5EE',
              color: '#085041',
              fontSize: 12,
              fontWeight: 700,
              padding: '3px 10px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
            }}>
              {dynTotal} {dynTotal === 1 ? 'problem' : 'problems'}
            </span>
          )}
          <span style={{ fontSize: 14, color: '#478475' }}>›</span>
        </div>
      </button>

      {/* Slot: trial banner and tab bar live above/below this component */}
    </div>
  );
};

export default QuantTracker;
